import type React from 'react';
import { type 
  CSSProperties,type 
  Dispatch,type 
  HTMLProps,type 
  RefObject,type 
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import zIndex from '@/styles/zIndex';

import { CenterFlex, Flex } from '../base';
import Orderer from './Orderer/Orderer';

const ORDER_ORDER: OrderStatusT[] = ['desc', 'asc'];

type Props = HTMLProps<HTMLDivElement> & {
  column: DataTableColumnT;
  domIndex?: number;
  domColumnConfigs?: ColumnConfigsResT[];
  onResizeState?: RefObject<{
    target: string;
    width: number;
  } | null>;
  onChangeOrderState?: {
    target: string;
    initialOrder?: number;
    order?: number;
  } | null;
  setOnResizeState?: (
    state: {
      target: string;
      width: number;
      element: HTMLDivElement;
    } | null,
  ) => void;
  setOnChangeOrderState?: (
    state: {
      target: string;
      initialOrder?: number;
      order?: number;
    } | null,
  ) => void;
  onColumnMouseOver?: (
    e: React.MouseEvent,
    {
      isDefaultColumn,
      columnKey,
      domIndex,
    }: {
      isDefaultColumn?: boolean;
      columnKey: string;
      domIndex?: number;
      target?: HTMLDivElement | null;
    },
  ) => void;
  setDomColumnConfigs?: Dispatch<SetStateAction<ColumnConfigsResT[]>>;
  columnResizeHandler?: (key: string, width: number) => void;
  columnChangeOrderHandler?: (key: string, order: number) => void;
  fixed?: boolean;
  offset?: number;
};

const DataColumnItem = (props: Props) => {
  const { setDomColumnConfigs } = props;
  const columnRef = useRef<HTMLTableCellElement>(null);
  const color = useColor();

  const changeDomColumnWidth = useCallback(
    (key: string, width: number) => {
      setDomColumnConfigs?.((prev) => {
        const newConfigs = [...prev];
        const targetIndex = newConfigs.findIndex(
          (config) => config.name === key,
        );
        if (targetIndex === -1) return newConfigs;
        newConfigs[targetIndex].width = width;
        return newConfigs;
      });
    },
    [setDomColumnConfigs],
  );

  const nextOrder = useCallback(() => {
    if (!props.column.orderable) return;
    const currIndex = ORDER_ORDER.findIndex(
      (order) => order === props.column.orderStatus,
    );
    const nextIndex = (currIndex + 1) % ORDER_ORDER.length;
    props.column.onOrderClick?.({
      orderStatus: ORDER_ORDER[nextIndex],
      sortBy: props.column.columnKey,
    });
  }, [props]);

  const onResizeHandlerDragStart = (e: React.MouseEvent) => {
    if (!columnRef.current) return;
    e.stopPropagation();
    props.setOnResizeState?.({
      target: props.column.columnKey,
      width: columnRef.current.getBoundingClientRect().width,
      element: columnRef.current,
    });
  };

  const onColumnDragStart = (e: React.MouseEvent) => {
    if (props.column.isDefaultColumn || props.onResizeState?.current) return;
    document.body.style.cursor = 'grabbing';

    props.setOnChangeOrderState?.({
      target: props.column.columnKey,
      initialOrder: props.column.columnOrderIndex,
    });
  };

  useEffect(() => {
    changeDomColumnWidth(props.column.columnKey, props.column.width || 200);
  }, [changeDomColumnWidth, props.column.columnKey, props.column.width]);

  const columnStyle: CSSProperties = {
    fontSize: props.style?.fontSize || 12,
    color: props.style?.color || color.gray600,
    fontWeight: props.style?.fontWeight || 400,
    userSelect: 'none',
    ...props.column.columnStyle,
  };

  return (
    <DefaulColumnHeaderStyle
      style={{
        ...props.style,
        width: props.column.width,
        flex: props.column.responsive ? 1 : 'unset',
      }}
      className={props.className}
      isColumnDragging={
        props.onChangeOrderState?.target === props.column.columnKey
      }
      onMouseMove={(e) =>
        props.onColumnMouseOver?.(e, {
          isDefaultColumn: props.column.isDefaultColumn,
          columnKey: props.column.columnKey,
          domIndex: props.domIndex,
        })
      }
      ref={columnRef}
      fixed={props.fixed}
      offset={props.offset}
    >
      <Flex a="center" g="8px">
        {!props.column.isDefaultColumn && (
          <CenterFlex className="handler-icon" onMouseDown={onColumnDragStart}>
            <HandlerIcon color={color.gray500} />
          </CenterFlex>
        )}
        {!props.column.hiddenColumnName &&
          (props.column.getHeaderNode ? (
            <Flex style={columnStyle}>{props.column.getHeaderNode()}</Flex>
          ) : (
            <Typo style={columnStyle}>{props.column.columnName}</Typo>
          ))}
        <Orderer
          orderStatus={props.column.orderStatus}
          orderable={props.column.orderable}
          nextOrder={nextOrder}
        />
        {!props.column.isDefaultColumn && (
          <ResizeHandler onMouseDown={onResizeHandlerDragStart} />
        )}
      </Flex>
    </DefaulColumnHeaderStyle>
  );
};

type StyleProps = {
  isColumnDragging?: boolean;
  fixed?: boolean;
  offset?: number;
};

export const DefaulColumnHeaderStyle = styled(Flex).attrs({
  a: 'center',
})<StyleProps>`
  position: relative;
  padding: 0 8px;
  height: 100%;
  background-color: ${(props) =>
    props.isColumnDragging
      ? props.theme.colors.primaryAlpha(0.2)
      : props.theme.colors.gray100};
  transition: background-color 0.2s;
  text-align: left;

  .handler-icon {
    display: none;
    right: left;
    cursor: grab;
  }
  &:first-child {
    border: none;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.gray200};
    box-shadow: inset -1px 0 0 0 ${(props) => props.theme.colors.gray400};
    .handler-icon {
      display: block;
    }
  }
  ${(props) =>
    props.fixed &&
    css`
      position: sticky;
      left: ${props.offset || 0}px;
      background-color: ${props.theme.colors.gray100};
      z-index: 1;
    `}
`;

const ResizeHandler = styled.div`
  position: absolute;
  z-index: ${zIndex.innerResizebar};
  right: 0px;
  width: 10px;
  height: 100%;
  cursor: ew-resize;
`;

export default DataColumnItem;
